import { useState, useCallback, useEffect, useRef } from 'react';
import { ShippingServiceFilters } from 'src/use-cases/serviceProvider/useServiceProvider';
import { Modality } from 'src/types';

/**
 * Custom hook to manage shipping service filter states
 * This helps centralize filter management for the shipping service API
 */
export function useShippingServiceFilters(initialModality: Modality, initialServiceProvider: string) {
  // Store initial props to detect when they've been loaded
  const initialPropsRef = useRef({ modality: initialModality, serviceProvider: initialServiceProvider });
  const hasPropsChanged = useRef(false);

  // Track if filters are ready for API calls
  const [isReady, setIsReady] = useState(false);

  // Filter states - initialize with URL params if available
  const [filters, setFilters] = useState<ShippingServiceFilters>(() => ({
    page: 0,
    size: 100,
    modalities: initialModality ? [initialModality] : [],
    serviceProviderCodes: initialServiceProvider ? [initialServiceProvider] : [],
  }));

  // Update filters when URL params change, but only if values actually changed
  useEffect(() => {
    setFilters((prev) => {
      const newModalities = initialModality ? [initialModality] : [];
      const newServiceProviderCodes = initialServiceProvider ? [initialServiceProvider] : [];

      // Check if values actually changed to prevent unnecessary updates
      const modalitiesChanged = JSON.stringify(prev.modalities) !== JSON.stringify(newModalities);
      const serviceProviderChanged = JSON.stringify(prev.serviceProviderCodes) !== JSON.stringify(newServiceProviderCodes);

      if (!modalitiesChanged && !serviceProviderChanged) {
        return prev; // No change, return same object to prevent re-render
      }

      return {
        ...prev,
        modalities: newModalities,
        serviceProviderCodes: newServiceProviderCodes,
      };
    });

    // Check if props have changed from initial values
    if (initialModality !== initialPropsRef.current.modality || initialServiceProvider !== initialPropsRef.current.serviceProvider) {
      hasPropsChanged.current = true;
    }

    // Enable API calls if:
    // 1. Props have changed (means parent loaded URL params), OR
    // 2. Initial props were already set (direct navigation with params)
    if (hasPropsChanged.current || initialModality || initialServiceProvider) {
      setIsReady(true);
    } else {
      // If props haven't changed and are still null, wait a bit for parent to load them
      const timer = setTimeout(() => {
        setIsReady(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [initialModality, initialServiceProvider]);

  // Update a specific filter field
  const updateFilter = useCallback(<K extends keyof ShippingServiceFilters>(key: K, value: ShippingServiceFilters[K]) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Update multiple filters at once
  const updateFilters = useCallback((newFilters: Partial<ShippingServiceFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  // Reset all filters to default values
  const resetFilters = useCallback((modality: Modality) => {
    setFilters({
      modalities: [modality],
      page: 0,
      size: 100,
    });
  }, []);

  // Update pagination
  const updatePagination = useCallback((page: number, size?: number) => {
    console.log('updatePagination called with page:', page, 'size:', size);
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        page, // page is 0-based in the API
        ...(size !== undefined ? { size } : {}),
      };
      console.log('New filters after pagination update:', newFilters);
      return newFilters;
    });
  }, []);

  // Update sorting
  const updateSorting = useCallback((sortBy: string, sortDirection: 'ASC' | 'DESC' = 'ASC') => {
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortDirection,
    }));
  }, []);

  return {
    filters,
    isReady,
    updateFilter,
    updateFilters,
    resetFilters,
    updatePagination,
    updateSorting,
  };
}
